import {
  Button,
  ModalContent,
  Input,
  Label,
  Textarea,
  Checkbox,
  DatePicker,
  Form,
  FormField,
  FormControl,
  FormItem,
  FormMessage,
} from '@sandtech-hub-monorepo/ui-kit';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CityPicker,
  StatePicker,
  CountryPicker,
} from '@sandtech-hub-monorepo/ui-kit-site';
import { ReactComponent as FileCheckIcon } from '../../../../assets/Icons/file-check.svg';
import {
  UpdateProgramApplicationHighSchoolCertificateDocument,
  UpdateProgramApplicationHighSchoolCertificateMutation,
  UpdateProgramApplicationHighSchoolCertificateMutationVariables,
} from '@sandtech-hub-monorepo/ehub-client';
import { useMutation } from '@apollo/client';
import { VerificationModalHeader } from './VerificationModalHeader';

const formSchema = z.object({
  schoolName: z.string().min(1, 'School name is required'),
  country: z.string().min(1, 'Country is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  graduationDate: z.date({ required_error: 'Graduation date is required' }),
  schoolAddress: z.string().min(1, 'School address is required'),
  qualification: z.string().min(1, 'Qualification is required'),
  fullName: z.string().min(1, 'Full name is required'),
  confirmInformation: z.boolean().refine((val) => val, {
    message: 'You must confirm the information is correct',
  }),
});

type FormData = z.infer<typeof formSchema>;

interface ManualHighSchoolCertificateFormProps {
  programApplicationId: string;
  close: () => void;
}

export const ManualHighSchoolCertificateForm = ({
  programApplicationId,
  close,
}: ManualHighSchoolCertificateFormProps) => {
  const [showSuccess, setShowSuccess] = useState(false);

  const [updateCertificate] = useMutation<
    UpdateProgramApplicationHighSchoolCertificateMutation,
    UpdateProgramApplicationHighSchoolCertificateMutationVariables
  >(UpdateProgramApplicationHighSchoolCertificateDocument);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      schoolName: '',
      country: '',
      city: '',
      state: '',
      graduationDate: undefined,
      schoolAddress: '',
      qualification: '',
      fullName: '',
      confirmInformation: false,
    },
  });

  const handleManualSubmit = async (data: FormData) => {
    try {
      await updateCertificate({
        variables: {
          input: {
            programApplicationId,
            schoolName: data.schoolName,
            country: data.country,
            city: data.city,
            state: data.state,
            graduationDate: data.graduationDate,
            schoolAddress: data.schoolAddress,
            qualification: data.qualification,
          },
        },
        refetchQueries: ['GetViewerProgramEnrollmentsWithApplication'],
      });
      setShowSuccess(true);
    } catch (error) {
      console.error('Error submitting high school certificate:', error);
      // TODO: Show error message to user
    }
  };

  if (showSuccess) {
    return (
      <ModalContent>
        <div className="flex flex-col items-center text-center gap-6 max-w-[422px] mx-auto">
          <VerificationModalHeader
            icon={<FileCheckIcon height="48" width="48" />}
            title="High School Certificate Verification"
            description="Thank you for submitting your school graduation details."
          />
          <div className="flex justify-center">
            <Button onClick={close}>OK</Button>
          </div>
        </div>
      </ModalContent>
    );
  }

  return (
    <ModalContent>
      <VerificationModalHeader
        icon={<FileCheckIcon height="48" width="48" />}
        title="High School Certificate Verification"
      />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleManualSubmit)}
          className="flex flex-col gap-4 max-w-[422px] mx-auto mt-8"
        >
          {/* School Name */}
          <FormField
            control={form.control}
            name="schoolName"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1.5">
                <Label className="text-sm font-medium text-muted-foreground">
                  Enter name of the school *
                </Label>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    placeholder="Enter school name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Country */}
          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1.5">
                <Label className="text-sm font-medium text-muted-foreground">
                  Country *
                </Label>
                <FormControl>
                  <CountryPicker
                    selected={field.value}
                    handleOnSelect={({ value }) => {
                      if (value !== field.value) {
                        field.onChange(value);
                        form.setValue('state', '');
                        form.setValue('city', '');
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* State and City */}
          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem className="flex-1 flex flex-col gap-1.5">
                  <Label className="text-sm font-medium text-muted-foreground">
                    State *
                  </Label>
                  <FormControl>
                    <StatePicker
                      countryCode={form.watch('country')}
                      selected={field.value}
                      handleOnSelect={({ value }) => {
                        if (value !== field.value) {
                          field.onChange(value);
                          form.setValue('city', '');
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem className="flex-1 flex flex-col gap-1.5">
                  <Label className="text-sm font-medium text-muted-foreground">
                    City *
                  </Label>
                  <FormControl>
                    <CityPicker
                      countryCode={form.watch('country')}
                      stateCode={form.watch('state')}
                      selected={field.value}
                      handleOnSelect={({ value }) => field.onChange(value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Graduation Date */}
          <FormField
            control={form.control}
            name="graduationDate"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1.5">
                <Label className="text-sm font-medium text-muted-foreground">
                  Graduation date *
                </Label>
                <FormControl>
                  <DatePicker
                    selected={field.value}
                    onSelect={field.onChange}
                    placeholder="Select graduation date"
                    disabledDates={(date) => date > new Date()}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* School Address */}
          <FormField
            control={form.control}
            name="schoolAddress"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1.5">
                <Label className="text-sm font-medium text-muted-foreground">
                  Enter school address *
                </Label>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Enter school address"
                    className="min-h-[86px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Qualification */}
          <FormField
            control={form.control}
            name="qualification"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1.5">
                <Label className="text-sm font-medium text-muted-foreground">
                  Enter your qualification *
                </Label>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    placeholder="Enter your qualification"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Full Name with "I," prefix */}
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1.5">
                <Label className="text-sm font-medium text-muted-foreground">
                  Full Name *
                </Label>
                <FormControl>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground">I,</span>
                    <Input
                      {...field}
                      type="text"
                      placeholder="Enter your full name"
                      className="flex-1"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Confirmation Checkbox */}
          <FormField
            control={form.control}
            name="confirmInformation"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-start gap-3">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-0.5"
                    />
                  </FormControl>
                  <span className="text-sm text-foreground leading-normal">
                    I confirm that the information I provided is correct to the
                    best of my knowledge and give permission to FIT to verify
                    this information.
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-center mt-8">
            <Button
              type="submit"
              className="px-8"
              disabled={!form.formState.isValid}
            >
              Submit
            </Button>
          </div>
        </form>
      </Form>
    </ModalContent>
  );
};
